import { useState, useMemo, useEffect } from 'react';
import { Icon } from '@iconify/react';
import { formatToBRL } from 'base/src/utils/mathUtil';
import { formatDateTime } from 'base/src/utils/stringUtil';
import { useTheme } from 'base/src/utils/context/themeContext';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import TablePagination from '@mui/material/TablePagination';
import Paper from '@mui/material/Paper';
import { ExtratoResponse } from 'domain/types/_carteira/extrato/extratoResponse';

interface ExtratoListProps {
  extrato: ExtratoResponse[] | undefined
  hideValues: boolean
}

function ExtratoList({ extrato, hideValues = false }: ExtratoListProps) {
  const { theme } = useTheme();
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);

  useEffect(function resetPageOnDataChange() {
    setPage(0);
  }, [extrato]);

  function handleChangePage(_event: unknown, newPage: number) {
    setPage(newPage);
  }

  function handleChangeRowsPerPage(event: React.ChangeEvent<HTMLInputElement>) {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  }

  function labelDisplayedRows({ from, to, count }: { from: number; to: number; count: number }) {
    return `${from}\u2013${to} de ${count !== -1 ? count : `mais de ${to}`}`;
  }

  const paginatedExtrato = useMemo(function computePaginatedData() {
    if (!extrato) return [];
    if (rowsPerPage === -1) return extrato;
    const start = page * rowsPerPage;
    return extrato.slice(start, start + rowsPerPage);
  }, [extrato, page, rowsPerPage]);

  function getStatusColor(status: string) {
    if (status === 'Pendente') return theme.colors.warning;
    if (status === 'Cancelado') return theme.colors.negative;
    return theme.colors.positive;
  }

  const headerSx = {
    backgroundColor: 'transparent',
    color: theme.colors.grey_text,
    fontWeight: 600,
    fontSize: 11,
    textTransform: 'uppercase' as const,
    letterSpacing: '0.06em',
    padding: '16px 20px',
    borderBottom: `2px solid ${theme.colors.border_grey}40`,
    whiteSpace: 'nowrap' as const,
  };

  const cellSx = {
    color: theme.colors.text,
    fontSize: 13,
    fontWeight: 500,
    padding: '18px 20px',
    borderBottom: `1px solid ${theme.colors.border_grey}30`,
  };

  return (
    <Paper
      sx={{
        backgroundColor: theme.colors.card,
        borderRadius: '16px',
        overflow: 'hidden',
        boxShadow: '0 1px 3px rgba(0,0,0,0.04), 0 4px 24px rgba(0,0,0,0.06)',
        border: `1px solid ${theme.colors.border_grey}30`,
      }}
    >
      <TableContainer>
        <Table size="medium">
          <TableHead>
            <TableRow>
              <TableCell sx={headerSx} align="center">ID</TableCell>
              <TableCell sx={headerSx} align="left">Data</TableCell>
              <TableCell sx={headerSx} align="left">Descrição</TableCell>
              <TableCell sx={headerSx} align="center">Tipo</TableCell>
              <TableCell sx={headerSx} align="center">Status</TableCell>
              <TableCell sx={headerSx} align="right">Valor</TableCell>
              <TableCell sx={headerSx} align="right">Balanço</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {paginatedExtrato.map(function renderRow(item, index) {
              const isSaque = item.Tipo === 'Saque';
              const isPositive = item.ValorAdicionado > 0;
              const statusColor = getStatusColor(item.Status);
              return (
                <TableRow
                  key={item.DataOperacao || index}
                  sx={{
                    transition: 'all 150ms ease',
                    '&:hover': {
                      backgroundColor: theme.colors.hover,
                    },
                    '&:last-child td': { borderBottom: 'none' },
                  }}
                >
                  <TableCell sx={{ ...cellSx, color: theme.colors.grey_text, fontWeight: 600, fontVariantNumeric: 'tabular-nums' }} align="center">
                    {item.Id}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, whiteSpace: 'nowrap', fontVariantNumeric: 'tabular-nums' }} align="left">
                    {formatDateTime(item.DataOperacao)}
                  </TableCell>
                  <TableCell sx={{ ...cellSx, maxWidth: 260, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} align="left">
                    {item.Descricao}
                  </TableCell>
                  <TableCell sx={cellSx} align="center">
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: isSaque ? `${theme.colors.negative}12` : `${theme.colors.positive}12`,
                      color: isSaque ? theme.colors.negative : theme.colors.positive,
                    }}>
                      <Icon icon={isSaque ? 'ph:arrow-up-right-bold' : 'ph:arrow-down-left-bold'} height={12} />
                      {item.Tipo}
                    </span>
                  </TableCell>
                  <TableCell sx={cellSx} align="center">
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 5,
                      padding: '4px 12px',
                      borderRadius: 20,
                      fontSize: 11,
                      fontWeight: 600,
                      backgroundColor: `${statusColor}14`,
                      color: statusColor,
                    }}>
                      <span style={{
                        width: 6,
                        height: 6,
                        borderRadius: '50%',
                        backgroundColor: statusColor,
                      }} />
                      {item.Status}
                    </span>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }} align="right">
                    <span style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: 6,
                      color: isPositive ? theme.colors.positive : theme.colors.negative,
                    }}>
                      {isPositive ? '+' : ''}{hideValues ? 'R$ *****,**' : formatToBRL(item.ValorAdicionado || 0)}
                      <Icon
                        icon={isPositive ? 'ph:trend-up-bold' : 'ph:trend-down-bold'}
                        height={14}
                      />
                    </span>
                  </TableCell>
                  <TableCell sx={{ ...cellSx, fontVariantNumeric: 'tabular-nums', fontWeight: 600 }} align="right">
                    {item.NovoBalanco === 0 ? (
                      <span style={{
                        fontSize: 11,
                        fontWeight: 600,
                        color: theme.colors.warning,
                        padding: '3px 10px',
                        borderRadius: 20,
                        backgroundColor: `${theme.colors.warning}14`,
                      }}>
                        Pendente
                      </span>
                    ) : (
                      <span style={{ color: theme.colors.text }}>
                        {hideValues ? 'R$ *****,**' : formatToBRL(item.NovoBalanco)}
                      </span>
                    )}
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </TableContainer>
      <TablePagination
        component="div"
        labelDisplayedRows={labelDisplayedRows}
        labelRowsPerPage="Linhas por p\u00e1gina"
        rowsPerPageOptions={[10, 25, 50, { label: 'Todos', value: -1 }]}
        count={extrato?.length ?? 0}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
        sx={{
          color: theme.colors.grey_text,
          borderTop: `1px solid ${theme.colors.border_grey}30`,
          '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': {
            fontSize: 12,
            fontWeight: 500,
            color: theme.colors.grey_text,
          },
          '.MuiTablePagination-select': {
            color: theme.colors.text,
            fontWeight: 500,
          },
          '.MuiTablePagination-actions button': {
            color: theme.colors.text,
            '&:hover': { backgroundColor: theme.colors.hover },
          },
        }}
      />
    </Paper>
  );
}

export default ExtratoList;
